import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import LoadingSpinner from "../../../shared/components/LoadingSpinner";
import PhoneField from "../../../shared/components/form/PhoneField";
import { useEditUser } from "../hooks/useEditUser";
import NationalitySelect from "../../../shared/components/NationalitySelect";
import {
  EYE_COLOR_OPTIONS,
  HAIR_COLOR_OPTIONS,
  HAIR_TYPE_OPTIONS,
  SKIN_TONE_OPTIONS,
  BODY_TYPE_OPTIONS,
  LANGUAGE_OPTIONS,
  LOOK_ALIKE_OPTIONS,
  TALENT_TYPES,
} from "../../../shared/constants/talentOptions";
import { OTHER_LANGUAGE_VALUE } from "../../../shared/constants/languages";
import { adminAPI } from "../services/admin.api";
import AdminGallery from "../components/AdminGallery";
import { ASSET_BASE_URL } from "../../../constants/env";
import toast from "react-hot-toast";
import { useLanguage } from "../../../context/LanguageContext";

// ─── Types ────────────────────────────────────────────────────────────────────

interface TalentForm {
  code_name?: string;
  date_of_birth?: string;
  city?: string;
  experience?: number | string;
  rates_per?: string;
  nationality?: string;
  talent_type?: string;
  gender?: string;
  bio?: string;
  height?: number | string;
  weight?: number | string;
  t_shirt_size?: string;
  pant_size?: string;
  shoe_size?: string;
  eye_color?: string;
  hair_color?: string;
  hair_type?: string;
  skin_tone?: string;
  body_type?: string;
  languages?: string[];
  custom_language?: string;
  look_alike?: string;
  specialties?: Array<{ id?: string | number; name?: string; options?: { id: number; name: string }[] } | string>;
  specialty_options?: number[];
  categories?: Array<{ id?: string | number; name?: string } | string>;
  profile_image?: string;
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const IC =
  "w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none transition focus:border-[#b2d12e] focus:ring-4 focus:ring-[#b2d12e]/20";

// ─── Small UI helpers ─────────────────────────────────────────────────────────

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <h2 className="text-lg font-bold text-gray-800 mt-2">{children}</h2>
);

const SubTitle = ({ children }: { children: React.ReactNode }) => (
  <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mt-2">
    {children}
  </h3>
);

const Label = ({ children }: { children: React.ReactNode }) => (
  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
    {children}
  </label>
);

// ─── Storage URL helper ───────────────────────────────────────────────────────

const toStorageUrl = (path?: string | null): string | null => {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  return ASSET_BASE_URL + path.replace(/^\/+/, "");
};

// ─── Talent detail field definitions ─────────────────────────────────────────

const TALENT_TEXT_FIELDS = [
  { labelKey: "admin.editUser.codeName", key: "code_name", type: "text" },
  { labelKey: "admin.editUser.dateOfBirth", key: "date_of_birth", type: "date" },
  { labelKey: "admin.editUser.city", key: "city", type: "text" },
  { labelKey: "admin.editUser.experienceYears", key: "experience", type: "number" },
  { labelKey: "admin.editUser.ratesPer", key: "rates_per", type: "text" },
] as const;

const TALENT_SELECT_FIELDS = [
  { labelKey: "admin.editUser.eyeColor", key: "eye_color", options: EYE_COLOR_OPTIONS },
  { labelKey: "admin.editUser.hairColor", key: "hair_color", options: HAIR_COLOR_OPTIONS },
  { labelKey: "admin.editUser.hairType", key: "hair_type", options: HAIR_TYPE_OPTIONS },
  { labelKey: "admin.editUser.skinTone", key: "skin_tone", options: SKIN_TONE_OPTIONS },
  { labelKey: "admin.editUser.bodyType", key: "body_type", options: BODY_TYPE_OPTIONS },
  { labelKey: "admin.editUser.lookAlike", key: "look_alike", options: LOOK_ALIKE_OPTIONS },
] as const;

// ─── Component ────────────────────────────────────────────────────────────────

export default function EditUser() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const {
    loading,
    saving,
    form,
    setField,
    setTalentField,
    setClientField,
    save,
    id,
    galleryMedia,
    specialtiesOptions,
  } = useEditUser();

  const tal = form.talent as TalentForm | undefined;
  const c = form.client;

  const fileRef = useRef<HTMLInputElement>(null);
  const blobPreviewRef = useRef<string | null>(null); // FIX: use ref to track blob for cleanup

  const [blobPreview, setBlobPreview] = useState<string | null>(null);
  const [uploadedUrl, setUploadedUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [photoModal, setPhotoModal] = useState(false);

  // FIX: reset image state when the user being edited changes (id changed)
  useEffect(() => {
    setUploadedUrl(null);
    setBlobPreview(null);
    if (blobPreviewRef.current) {
      URL.revokeObjectURL(blobPreviewRef.current);
      blobPreviewRef.current = null;
    }
  }, [id]);

  // FIX: revoke blob URL on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (blobPreviewRef.current) {
        URL.revokeObjectURL(blobPreviewRef.current);
        blobPreviewRef.current = null;
      }
    };
  }, []);

  // FIX: memoized so toStorageUrl isn't called on every render
  const profileImageSrc = useMemo(
    () =>
      uploadedUrl ||
      blobPreview ||
      toStorageUrl(form.profile_image) ||
      toStorageUrl(tal?.profile_image) ||
      null,
    [uploadedUrl, blobPreview, form.profile_image, tal?.profile_image]
  );

  const handleImageChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !id) return;

      if (
        !["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(
          file.type
        )
      ) {
        toast.error(t("toast.user.unsupportedImageFormat"));
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error(t("toast.user.imageTooLarge"));
        return;
      }

      // FIX: revoke previous blob before creating a new one
      if (blobPreviewRef.current) {
        URL.revokeObjectURL(blobPreviewRef.current);
        blobPreviewRef.current = null;
      }

      const objectUrl = URL.createObjectURL(file);
      blobPreviewRef.current = objectUrl;
      setBlobPreview(objectUrl);
      setUploadedUrl(null);
      setUploading(true);

      try {
        const res = await adminAPI.uploadProfileImage(id, file);

        const normalized = toStorageUrl(res.profile_image_url) ?? objectUrl;

        setUploadedUrl(normalized);

        // FIX: revoke blob now that we have the permanent URL
        if (blobPreviewRef.current) {
          URL.revokeObjectURL(blobPreviewRef.current);
          blobPreviewRef.current = null;
        }
        setBlobPreview(null);

        toast.success(t("toast.user.profileImageUpdated"));
      } catch {
        toast.error(t("toast.user.imageUploadFailed"));
        setUploadedUrl(null);
        // blob preview stays visible so the user sees their selection
      } finally {
        setUploading(false);
        if (fileRef.current) fileRef.current.value = "";
      }
    },
    [id]
    // FIX: removed blobPreview from deps — we read it via blobPreviewRef instead
  );

  if (loading) return <LoadingSpinner />;

  return (
    <>
      <div className="min-h-screen bg-gray-50 p-4 sm:p-6 pt-20">
        <div className="max-w-5xl mx-auto bg-white p-6 rounded-3xl shadow space-y-0">

          {/* ── Header ──────────────────────────────────────────────── */}
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold">{t("admin.editUser.title")}</h1>
            <button
              onClick={() => navigate("/admin/users")}
              className="flex items-center gap-2 border px-4 py-2 rounded-xl hover:bg-gray-50 transition text-sm"
            >
              <ArrowLeftIcon className="h-4 w-4" /> {t("common.back")}
            </button>
          </div>

          {/* ── Profile Image ────────────────────────────────────────── */}
          <div className="flex items-center gap-5 pb-6 border-b">
            <div className="relative shrink-0">
              {/* FIX: added onClick to open the photo modal */}
              {profileImageSrc ? (
                <img
                  src={profileImageSrc}
                  alt={t("admin.editUser.profileImage")}
                  className="w-24 h-24 rounded-full object-cover cursor-pointer"
                  onClick={() => setPhotoModal(true)}
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                    e.currentTarget.nextElementSibling?.removeAttribute("style");
                  }}
                />
              ) : null}
              <div
                style={profileImageSrc ? { display: "none" } : undefined}
                className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center text-xs text-gray-500"
              >
                {t("admin.editUser.noImage")}
              </div>

              {uploading && (
                <div className="absolute inset-0 bg-white/70 rounded-full flex items-center justify-center">
                  <div className="w-4 h-4 border-2 border-[#b2d12e] border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-1">
                {t("admin.editUser.profileImage")}
              </p>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="text-sm px-4 py-2 rounded-xl border border-gray-300 hover:bg-gray-50 transition disabled:opacity-50"
              >
                {t("admin.editUser.changePhoto")}
              </button>
              <p className="text-xs text-gray-400 mt-1">
                {t("admin.editUser.imageHint")}
              </p>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                className="hidden"
                onChange={handleImageChange}
              />
            </div>
          </div>

          {/* ── Basic Information ────────────────────────────────────── */}
          <div className="grid gap-4 py-6 border-b">
            <SectionTitle>{t("admin.editUser.basicInformation")}</SectionTitle>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>{t("auth.firstName")}</Label>
                <input
                  className={IC}
                  value={form.first_name}
                  onChange={(e) => setField("first_name", e.target.value)}
                  placeholder={t("auth.firstName")}
                />
              </div>
              <div>
                <Label>{t("auth.lastName")}</Label>
                <input
                  className={IC}
                  value={form.last_name}
                  onChange={(e) => setField("last_name", e.target.value)}
                  placeholder={t("auth.lastName")}
                />
              </div>
              <div>
                <Label>{t("auth.email")}</Label>
                <input
                  type="email"
                  className={IC}
                  value={form.email}
                  onChange={(e) => setField("email", e.target.value)}
                  placeholder={t("auth.email")}
                />
              </div>
              <div>
                <Label>{t("admin.editUser.phone")}</Label>
                <PhoneField
                  value={form.phone}
                  onChange={(v) => setField("phone", v)}
                  onCountryChange={(c) => setField("phone_country", c ?? "")}
                />
              </div>
              <div>
                <Label>{t("common.status")}</Label>
                <select
                  className={IC}
                  value={form.status ?? "pending"}
                  onChange={(e) => setField("status", e.target.value)}
                >
                  <option value="pending">{t("admin.editUser.statusPending")}</option>
                  <option value="rejected">{t("admin.editUser.statusRejected")}</option>
                  <option value="approved">{t("admin.editUser.statusApproved")}</option>
                </select>
              </div>
            </div>
          </div>

          {/* ── Talent Section ───────────────────────────────────────── */}
          {form.role === "talent" && tal && (
            <>
              {/* Talent Details */}
              <div className="grid gap-4 py-6 border-b">
                <SectionTitle>{t("admin.editUser.talentDetails")}</SectionTitle>
                <div className="grid sm:grid-cols-2 gap-4">
                  {TALENT_TEXT_FIELDS.map(({ labelKey, key, type }) => (
                    <div key={key}>
                      <Label>{t(labelKey)}</Label>
                      <input
                        type={type}
                        className={IC}
                        value={(tal[key] as string | number | undefined) ?? ""}
                        onChange={(e) => setTalentField(key, e.target.value)}
                      />
                    </div>
                  ))}
                  <div>
                    <NationalitySelect
                      value={tal.nationality ?? ""}
                      onChange={(v) => setTalentField("nationality", v)}
                      className={IC}
                    />
                  </div>
                  <div>
                    <Label>{t("admin.editUser.talentType")}</Label>
                    <select
                      className={IC}
                      value={tal.talent_type ?? ""}
                      onChange={(e) =>
                        setTalentField("talent_type", e.target.value)
                      }
                    >
                      <option value="">{t("admin.editUser.selectTalentType")}</option>
                      {TALENT_TYPES.map((tt) => (
                        <option key={tt.value} value={tt.value}>
                          {tt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>{t("admin.editUser.gender")}</Label>
                    <select
                      className={IC}
                      value={tal.gender ?? ""}
                      onChange={(e) => setTalentField("gender", e.target.value)}
                    >
                      <option value="">{t("admin.editUser.selectGender")}</option>
                      <option value="male">{t("admin.editUser.male")}</option>
                      <option value="female">{t("admin.editUser.female")}</option>
                    </select>
                  </div>
                </div>
                <div>
                  <Label>{t("admin.editUser.bio")}</Label>
                  <textarea
                    className={IC}
                    rows={3}
                    value={tal.bio ?? ""}
                    onChange={(e) => setTalentField("bio", e.target.value)}
                    placeholder={t("admin.editUser.bioPlaceholder")}
                  />
                </div>
              </div>

              {/* Physical Attributes */}
              <div className="grid gap-4 py-6 border-b">
                <SectionTitle>{t("admin.editUser.physicalAttributes")}</SectionTitle>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>{t("admin.editUser.heightCm")}</Label>
                    <input
                      type="number"
                      className={IC}
                      value={tal.height ?? ""}
                      onChange={(e) => setTalentField("height", e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>{t("admin.editUser.weightKg")}</Label>
                    <input
                      type="number"
                      className={IC}
                      value={tal.weight ?? ""}
                      onChange={(e) => setTalentField("weight", e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>{t("admin.editUser.tShirtSize")}</Label>
                    <input
                      type="text"
                      className={IC}
                      value={tal.t_shirt_size ?? ""}
                      onChange={(e) => setTalentField("t_shirt_size", e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>{t("admin.editUser.pantSize")}</Label>
                    <input
                      type="text"
                      className={IC}
                      value={tal.pant_size ?? ""}
                      onChange={(e) => setTalentField("pant_size", e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>{t("admin.editUser.shoeSize")}</Label>
                    <input
                      type="text"
                      className={IC}
                      value={tal.shoe_size ?? ""}
                      onChange={(e) => setTalentField("shoe_size", e.target.value)}
                    />
                  </div>
                  {TALENT_SELECT_FIELDS.map(({ labelKey, key, options }) => (
                    <div key={key}>
                      <Label>{t(labelKey)}</Label>
                      <select
                        className={IC}
                        value={(tal[key] as string | undefined) ?? ""}
                        onChange={(e) => setTalentField(key, e.target.value)}
                      >
                        <option value="">{t("admin.editUser.selectField", { field: t(labelKey) })}</option>
                        {options.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>

              {/* Languages */}
              <div className="grid gap-4 py-6 border-b">
                <SubTitle>{t("admin.editUser.languages")}</SubTitle>
                <div className="flex flex-wrap gap-2 mb-2">
                  {(tal.languages ?? []).map((lang: string) => (
                    <span
                      key={lang}
                      className="inline-flex items-center gap-1 bg-[#b2d12e]/15 text-gray-800 text-xs font-medium px-3 py-1 rounded-full"
                    >
                      {lang}
                      <button
                        type="button"
                        onClick={() =>
                          setTalentField(
                            "languages",
                            (tal.languages ?? []).filter((l) => l !== lang)
                          )
                        }
                        className="text-gray-400 hover:text-red-500 transition ml-1"
                        aria-label={t("admin.editUser.removeLanguage", { lang })}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                <select
                  className={IC}
                  value=""
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val && !(tal.languages ?? []).includes(val)) {
                      setTalentField("languages", [
                        ...(tal.languages ?? []),
                        val,
                      ]);
                    }
                  }}
                >
                  <option value="">{t("admin.editUser.addLanguage")}</option>
                  {LANGUAGE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                {(tal.languages ?? []).includes(OTHER_LANGUAGE_VALUE) && (
                  <div>
                    <Label>{t("admin.editUser.otherLanguage")}</Label>
                    <input
                      className={IC}
                      value={tal.custom_language ?? ""}
                      onChange={(e) => setTalentField("custom_language", e.target.value)}
                      placeholder={t("admin.editUser.specifyLanguage")}
                    />
                  </div>
                )}
              </div>

              {/* Specialties */}
              {((tal.specialties?.length ?? 0) > 0 ||
                (tal.categories?.length ?? 0) > 0) && (
                  <div className="grid gap-4 py-6 border-b">
                    <SubTitle>{t("admin.editUser.specialties")}</SubTitle>
                    <div className="flex flex-wrap gap-2">
                      {(tal.specialties ?? tal.categories ?? []).map((s) => (
                        <span
                          key={typeof s === "object" ? s?.id ?? String(s) : s}
                          className="bg-gray-100 text-gray-700 text-xs font-medium px-3 py-1 rounded-full"
                        >
                          {typeof s === "object" ? s?.name : s}
                        </span>
                      ))}
                    </div>

                    {/* Sub-options for any of the talent's specialties that have them
                        (e.g. Dancer → dance styles, Sport → sports) */}
                    {(tal.specialties ?? [])
                      .filter((s): s is { id?: string | number; name?: string } => typeof s === "object")
                      .map((s) => {
                        const master = specialtiesOptions.find((so) => so.id === Number(s?.id));
                        if (!master?.options?.length) return null;
                        return (
                          <div key={master.id} className="mt-2">
                            <Label>{t("admin.editUser.specialization", { name: master.name })}</Label>
                            <div className="flex flex-wrap gap-2">
                              {master.options.map((o) => {
                                const selected = (tal.specialty_options ?? []).includes(o.id);
                                return (
                                  <button
                                    key={o.id}
                                    type="button"
                                    onClick={() => {
                                      const current = tal.specialty_options ?? [];
                                      setTalentField(
                                        "specialty_options",
                                        selected
                                          ? current.filter((id) => id !== o.id)
                                          : [...current, o.id],
                                      );
                                    }}
                                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                                      selected
                                        ? "bg-[#b2d12e] text-gray-900"
                                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                    }`}
                                  >
                                    {o.name}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}

            </>
          )}

          {/* ── Client Section ───────────────────────────────────────── */}
          {form.role === "client" && (
            <div className="grid gap-4 py-6 border-b">
              <SectionTitle>{t("admin.editUser.clientInformation")}</SectionTitle>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>{t("admin.editUser.clientTitle")}</Label>
                  <input
                    className={IC}
                    value={c?.title ?? ""}
                    onChange={(e) => setClientField("title", e.target.value)}
                  />
                </div>
                <div>
                  <Label>{t("admin.editUser.company")}</Label>
                  <input
                    className={IC}
                    value={c?.company ?? ""}
                    onChange={(e) => setClientField("company", e.target.value)}
                  />
                </div>
                <div>
                  <Label>{t("admin.editUser.companyType")}</Label>
                  <input
                    className={IC}
                    value={c?.company_type ?? ""}
                    onChange={(e) => setClientField("company_type", e.target.value)}
                  />
                </div>
                <div>
                  <Label>{t("admin.editUser.whatsappNumber")}</Label>
                  <PhoneField
                    value={c?.whatsapp_number ?? ""}
                    onChange={(v) => setClientField("whatsapp_number", v)}
                  />
                </div>
              </div>
            </div>
          )}

          {/* ── Gallery ─────────────────────────────────────────────── */}
          {id && (
            <div className="py-6 border-b">
              <SectionTitle>{t("admin.editUser.gallery")}</SectionTitle>
              <div className="mt-4">
                <AdminGallery userId={id} initialMedia={galleryMedia} />
              </div>
            </div>
          )}

          {/* ── Actions ─────────────────────────────────────────────── */}
          <div className="flex justify-end gap-3 pt-6">
            <button
              onClick={() => navigate("/admin/users")}
              className="border px-5 py-2 rounded-xl hover:bg-gray-50 transition text-sm"
            >
              {t("common.cancel")}
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="bg-[#b2d12e] text-gray-900 px-5 py-2 rounded-xl font-semibold hover:shadow-md transition disabled:opacity-50 text-sm"
            >
              {saving ? t("common.saving") : t("admin.editUser.saveChanges")}
            </button>
          </div>
        </div>
      </div>

      {/* ── Photo enlarge modal ──────────────────────────────────────── */}
      {photoModal && profileImageSrc && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/75"
          onClick={() => setPhotoModal(false)}
        >
          <img
            src={profileImageSrc}
            onError={(e) => (e.currentTarget.src = "/placeholder.png")}
            className="max-w-[90vw] max-h-[90vh] rounded-2xl object-contain shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            alt={t("admin.editUser.profileEnlarged")}
          />
          <button
            onClick={() => setPhotoModal(false)}
            className="absolute top-4 right-4 text-white bg-black/50 hover:bg-black/70 rounded-full w-9 h-9 flex items-center justify-center text-lg transition"
            aria-label={t("common.close")}
          >
            ×
          </button>
        </div>
      )}
    </>
  );
}